/**
 * Core Umami analytics
 *
 * All tracking flows through track(name, params) which calls umami.track().
 * Umami Cloud: https://cloud.umami.is
 * Website ID: 80418aa8-493f-40d0-ac33-6c58df26fd81
 */

import { observeWebVitals } from './vitals';

/**
 * Send a named event to Umami with optional parameters.
 */
/* No analytics provider is loaded on writerslogic.com today: no script tag,
   no dynamic injection, and `cloud.umami.is` survives only in a sourcemap. Every
   call below is therefore a no-op in production, verified against the live
   bundle on 2026-08-26.

   BEFORE wiring up a provider, update src/pages/PrivacyPage.tsx: the
   "Cookies and browser storage" bullet, the "Analytics" card under how-we-use-it,
   the "Analytics data" retention entry, and the third-party list, which no
   longer names a provider. Shipping a beacon without that makes the policy
   false the moment it deploys. */
export function track(eventName: string, params?: Record<string, unknown>): void {
  if (typeof window === 'undefined') return;
  if (typeof window.umami?.track === 'function') {
    window.umami.track(eventName, params);
  }
}

/**
 * Set user properties as an Umami event
 */
export function setUserProperties(properties: {
  subscription_tier?: 'free' | 'pro_monthly' | 'pro_annual' | 'founding';
  signup_date?: string;
  total_analyses?: number;
  total_words_analyzed?: number;
  preferred_theme?: 'light' | 'dark';
  user_segment?: string;
  has_exported?: boolean;
  has_shared?: boolean;
}): void {
  track('user_properties', properties);
}

/**
 * Track page view for SPA route changes.
 * Umami auto-tracks initial loads via the script tag.
 */
export function trackPageView(
  pagePath: string,
  pageTitle: string,
  additionalParams?: Record<string, unknown>
): void {
  track('page_view', {
    page_path: pagePath,
    page_title: pageTitle,
    ...additionalParams,
  });
}

/**
 * Track session start
 */
export function trackSessionStart(params?: {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  landing_page?: string;
  referrer?: string;
}): void {
  track('session_start', {
    ...params,
    referrer: params?.referrer || (typeof document !== 'undefined' ? document.referrer : ''),
  });
}

// ── Initialization ──────────────────────────────────────────────────────

let isInitialized = false;
let initPromise: Promise<void> | null = null;
let webVitalsCleanup: (() => void) | null = null;
let deferredTrackingId: number | null = null;
let deferredTrackingTimeoutId: ReturnType<typeof setTimeout> | null = null;

function doInitializeAnalytics(): void {
  trackSessionStart({
    landing_page: window.location.pathname,
    referrer: document.referrer,
    utm_source: new URLSearchParams(window.location.search).get('utm_source') || undefined,
    utm_medium: new URLSearchParams(window.location.search).get('utm_medium') || undefined,
    utm_campaign: new URLSearchParams(window.location.search).get('utm_campaign') || undefined,
  });

  if ('requestIdleCallback' in window) {
    deferredTrackingId = window.requestIdleCallback(() => {
      webVitalsCleanup = observeWebVitals();
    });
  } else {
    deferredTrackingTimeoutId = setTimeout(() => {
      webVitalsCleanup = observeWebVitals();
    }, 2000);
  }
}

export function initializeAnalytics(): Promise<void> {
  if (isInitialized) return Promise.resolve();
  if (initPromise) return initPromise;

  initPromise = new Promise<void>((resolve) => {
    if (typeof window === 'undefined') {
      resolve();
      return;
    }

    doInitializeAnalytics();
    isInitialized = true;
    resolve();
  });

  return initPromise;
}

export function cleanupAnalytics(): void {
  if (webVitalsCleanup) {
    webVitalsCleanup();
    webVitalsCleanup = null;
  }
  if (deferredTrackingId && 'cancelIdleCallback' in window) {
    window.cancelIdleCallback(deferredTrackingId);
    deferredTrackingId = null;
  }
  if (deferredTrackingTimeoutId) {
    clearTimeout(deferredTrackingTimeoutId);
    deferredTrackingTimeoutId = null;
  }
  isInitialized = false;
  initPromise = null;
}
